import {
  AuthorizationReview,
  getRequestedMcpAccessLevel,
} from '@/components/oauth/authorization-review';
import { ConsentDecisionActions, ConsentFooter } from '@/components/oauth/consent-footer';
import { ConsentScreen } from '@/components/oauth/consent-screen';
import { ResourceScopeNote } from '@/components/oauth/resource-scope-note';
import { WorkspaceAccessPicker } from '@/components/oauth/workspace-access-picker';
import { Button } from '@/components/ui/button';
import { FullPageSpinner } from '@/components/ui/full-page-spinner';
import { Input } from '@/components/ui/input';
import { useConsentGrant } from '@/hooks/use-consent-grant';
import { useLoginRedirect } from '@/hooks/use-login-redirect';
import type { OrgEntry } from '@/hooks/use-org-preselect';
import { API_URL } from '@/lib/api/core';
import { useAuthState } from '@/lib/auth-state';
import { oauthErrorMessage, redirectToOAuthLogin } from '@/lib/oauth-consent';
import { pruneSearch } from '@/lib/router-search';
import { workspaceLabel } from '@/lib/workspace-label';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

type DeviceSearchParams = {
  user_code?: string;
  return_to?: string;
};

type OAuthErrorFields = {
  error?: unknown;
  error_description?: unknown;
};

type DeviceInfoResponse = OAuthErrorFields & {
  client_name?: string | null;
  client_id?: string | null;
  logo_uri?: unknown;
  client_uri?: unknown;
  scopes?: unknown;
  resource?: unknown;
  multi_workspace_grants_enabled?: unknown;
};

type DeviceApproveResponse = OAuthErrorFields & {
  status?: unknown;
  user_code?: unknown;
};

type VerifiedDeviceCode = {
  code: string;
  clientName: string | null;
  logoUri: string | null;
  clientUri: string | null;
  scopes: string[];
  resource: string | null;
  multiWorkspaceGrantsEnabled: boolean;
};

const EMPTY_SCOPES: string[] = [];

// The device code is displayed and compared uppercase, but the server echoes
// back whatever casing the caller sent, so both sides are normalized before
// they are compared.
function normalizeUserCode(value: string) {
  return value.trim().toUpperCase();
}

const DEVICE_INFO_TIMEOUT_MS = 15_000;

async function getDeviceInfo(userCode: string) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), DEVICE_INFO_TIMEOUT_MS);
  try {
    const response = await fetch(
      `${API_URL}/oauth/device/info?user_code=${encodeURIComponent(userCode)}`,
      { credentials: 'include', signal: controller.signal },
    );
    const data = (await response.json().catch(() => null)) as DeviceInfoResponse | null;
    return { response, data };
  } catch (error) {
    if (controller.signal.aborted) {
      throw new Error('Verification timed out. Check your connection and try again.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

function sanitizeReturnTo(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  try {
    const url = new URL(trimmed);
    // Native deep-link schemes the consent page may redirect back to:
    // `lobu:` (Lobu for Mac, the scheme Lobu.app registers) and
    // `lobu-ios-bridge:` (iOS bridge).
    return url.protocol === 'lobu:' || url.protocol === 'lobu-ios-bridge:' ? trimmed : undefined;
  } catch {
    return undefined;
  }
}

function deviceGrantSummary({
  isVerified,
  requiresDeviceHome,
  requiresWorkspaceAccess,
  homeLabel,
  workspaceCount,
}: {
  isVerified: boolean;
  requiresDeviceHome: boolean;
  requiresWorkspaceAccess: boolean;
  homeLabel: string | null;
  workspaceCount: number;
}): string | null {
  if (!isVerified) return null;
  // Workspaces are still loading (or failed): the picker carries that state,
  // and "0 workspaces" would misstate a grant that is not resolved yet.
  if (requiresWorkspaceAccess && workspaceCount === 0) return null;
  const workspaces = `${workspaceCount} workspace${workspaceCount === 1 ? '' : 's'}`;
  if (requiresDeviceHome) {
    if (!homeLabel) return null;
    return requiresWorkspaceAccess
      ? `Pairs this device in ${homeLabel} and grants MCP access to ${workspaces}.`
      : `Pairs this device in ${homeLabel} with no workspace data access.`;
  }
  return requiresWorkspaceAccess ? `Grants MCP access to ${workspaces}.` : null;
}

export const Route = createFileRoute('/oauth/device')({
  component: DeviceConsentPage,
  validateSearch: (search: Record<string, unknown>): DeviceSearchParams =>
    pruneSearch({
      user_code:
        typeof search.user_code === 'string' && search.user_code.trim().length > 0
          ? search.user_code.trim()
          : undefined,
      return_to: sanitizeReturnTo(search.return_to),
    }),
});

function DeviceConsentPage() {
  const search = Route.useSearch();
  const { session, isReady: authReady } = useAuthState();
  const sessionLoading = !authReady;
  const [userCode, setUserCode] = useState(search.user_code || '');
  const [submitState, setSubmitState] = useState<'verify' | 'approve' | 'deny' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The outcome carries the exact code and client it applies to, so the
  // success screen can be correlated with the CLI's poller.
  const [done, setDone] = useState<{
    outcome: 'approved' | 'denied';
    code: string;
    clientName: string | null;
    deviceHomeLabel: string | null;
  } | null>(null);
  const [verification, setVerification] = useState<VerifiedDeviceCode | null>(null);
  // The workspace the user picked to home the device; null keeps the personal default.
  const [chosenDeviceHomeId, setChosenDeviceHomeId] = useState<string | null>(null);
  const verificationRequest = useRef(0);
  // Re-checked here, not trusted from validateSearch: TanStack Router merges a
  // route's validated search over the root's raw search, so a return_to the
  // validator dropped still arrives on `search` and would be navigated to.
  const returnTo = sanitizeReturnTo(search.return_to);
  const deviceLabel = returnTo ? 'app' : 'terminal';
  // Arriving with the code prefilled means the user followed an access link or
  // a verification_uri_complete rather than typing a code off a device — so
  // frame it as approving an access request.
  const fromLink = Boolean(search.user_code);
  const normalizedCode = normalizeUserCode(userCode);
  const activeVerification = verification?.code === normalizedCode ? verification : null;
  const isVerified = activeVerification !== null;
  const verifiedScopes = activeVerification?.scopes ?? EMPTY_SCOPES;
  const hasDeviceWorkerScope = verifiedScopes.includes('device_worker:run');
  const hasConnectionTokenScope = verifiedScopes.includes('connections:token');
  const multiWorkspaceGrantsEnabled = activeVerification?.multiWorkspaceGrantsEnabled === true;

  const {
    accessLevel,
    setAccessLevel,
    selectedScopes,
    resourceOrg,
    hasMcpScopes,
    requiresWorkspaceAccess,
    orgList,
    workspaceAccess,
    setWorkspaceAccess,
    selectedOrgIds,
    toggleSelectedOrgId,
    resetWorkspaceSelection,
    deviceHomeOrgId,
    orgsLoading,
    orgsError,
    retryOrgs,
    workspaceSelectionReady,
  } = useConsentGrant({
    requestedScopes: verifiedScopes,
    resource: activeVerification?.resource ?? null,
    allowMultiple: multiWorkspaceGrantsEnabled,
    requireDeviceHome: hasDeviceWorkerScope,
    deviceHomeOrgId: chosenDeviceHomeId,
  });

  const requiresDeviceHome = isVerified && hasDeviceWorkerScope && !resourceOrg;
  const deviceHomeOrg = orgList.find((org) => org.id === deviceHomeOrgId) ?? null;
  // Only show a workspace control when there is a real choice to make. A
  // single workspace needs neither the home select nor the picker. Loading and
  // failure still render the control so its status (and retry) is visible.
  const workspaceChoicesVisible = orgsLoading || orgsError !== null || orgList.length > 1;
  const soleOrg = orgList.length === 1 ? orgList[0] : undefined;
  const soleOrgNeedsHomeWarning = soleOrg !== undefined && !soleOrg.isPersonal;
  // Keep the home selector until a home is resolved: a member with a single
  // team workspace and no personal workspace has no default, and hiding the
  // selector would leave Approve disabled forever. Keep it for a sole team
  // workspace too, so its privacy warning stays visible.
  const showDeviceHome =
    requiresDeviceHome &&
    (workspaceChoicesVisible || deviceHomeOrgId === null || soleOrgNeedsHomeWarning);
  const showWorkspacePicker =
    requiresWorkspaceAccess &&
    workspaceChoicesVisible &&
    (multiWorkspaceGrantsEnabled || !requiresDeviceHome);
  const organizationResolutionReady =
    (!requiresWorkspaceAccess && !requiresDeviceHome) || workspaceSelectionReady;

  const verifyDeviceCode = useCallback(
    async (code: string) => {
      const requestId = ++verificationRequest.current;
      setSubmitState('verify');
      setError(null);

      try {
        const { response, data } = await getDeviceInfo(code);
        if (requestId !== verificationRequest.current) return;
        if (response.status === 401) {
          redirectToOAuthLogin();
          return;
        }
        if (!response.ok || !data) {
          throw new Error(oauthErrorMessage(data, 'Failed to verify device authorization code'));
        }
        const scopes = Array.isArray(data.scopes) ? (data.scopes as string[]) : [];
        setVerification({
          code,
          clientName: data.client_name ?? null,
          logoUri: typeof data.logo_uri === 'string' ? data.logo_uri : null,
          clientUri: typeof data.client_uri === 'string' ? data.client_uri : null,
          scopes,
          resource: typeof data.resource === 'string' ? data.resource : null,
          multiWorkspaceGrantsEnabled: data.multi_workspace_grants_enabled === true,
        });
        // Set the level with the verification so the first verified render
        // already has the requested access, instead of flashing "no access"
        // until the hook's reset effect runs.
        setAccessLevel(getRequestedMcpAccessLevel(scopes));
      } catch (err) {
        if (requestId !== verificationRequest.current) return;
        setVerification(null);
        setError(err instanceof Error ? err.message : 'Unexpected error');
      } finally {
        if (requestId === verificationRequest.current) setSubmitState(null);
      }
    },
    [setAccessLevel]
  );

  useLoginRedirect(!!session, sessionLoading);

  // Linked codes can be verified immediately. Typed codes use the same helper
  // from the Continue action below.
  useEffect(() => {
    if (sessionLoading || !session || !search.user_code) return;
    void verifyDeviceCode(normalizeUserCode(search.user_code));
    return () => {
      verificationRequest.current += 1;
    };
  }, [session, sessionLoading, search.user_code, verifyDeviceCode]);

  const submitConsent = async (approved: boolean) => {
    const code = normalizedCode;
    if (!code) {
      setError(`Please enter the code shown in your ${deviceLabel}.`);
      return;
    }
    if (!isVerified) {
      setError('Verify the device code before approving or denying access.');
      return;
    }
    if (approved && !organizationResolutionReady) {
      setError(
        orgsError
          ? 'Workspaces could not be loaded. Retry before approving.'
          : requiresDeviceHome && !deviceHomeOrg
            ? 'Choose a workspace for this device before approving.'
            : 'Choose at least one workspace before approving.',
      );
      return;
    }

    // The client name is read before the await: `activeVerification` is derived
    // from state that a code edit can clear while the request is in flight, and
    // the success screen must name the request this decision was made against.
    const decidedClientName = activeVerification?.clientName ?? null;
    const decidedHomeLabel =
      requiresDeviceHome && deviceHomeOrg ? workspaceLabel(deviceHomeOrg) : null;

    setSubmitState(approved ? 'approve' : 'deny');
    setError(null);

    try {
      const response = await fetch(`${API_URL}/oauth/device/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_code: code,
          approved,
          ...(approved ? { scope: selectedScopes.join(' ') } : {}),
          ...(approved && requiresWorkspaceAccess
            ? {
                workspace_access: workspaceAccess,
                organization_ids: selectedOrgIds,
              }
            : {}),
          // The device home. The server homes the device here and requires the
          // caller to be a member; the MCP grant above already includes it.
          ...(approved && requiresDeviceHome && deviceHomeOrg
            ? { organization_id: deviceHomeOrg.id }
            : {}),
        }),
      });

      const data = (await response.json().catch(() => null)) as DeviceApproveResponse | null;

      if (response.status === 401) {
        redirectToOAuthLogin();
        return;
      }

      if (!response.ok) {
        throw new Error(oauthErrorMessage(data, 'Failed to process device authorization'));
      }

      // A 2xx is NOT proof the device row reached its terminal state: issue
      // #3623 saw this page render "Access Authorized" while the CLI kept
      // polling `authorization_pending`. Only the exact expected status body
      // is success; anything else (empty, `{error}`, still-pending, a status
      // we do not recognize) keeps the code on screen and reports the real
      // state, so the verifier retries instead of walking away.
      const expectedStatus = approved ? 'approved' : 'denied';
      if (data?.status !== expectedStatus) {
        throw new Error(
          oauthErrorMessage(
            data,
            approved
              ? 'The server did not confirm this approval. The device is still waiting — try again.'
              : 'The server did not confirm this denial. Try again.'
          )
        );
      }
      // The paired server change has the approve route echo back the code it
      // wrote, verbatim as the caller sent it; the deny route returns no code.
      // So the check is conditional: when a code is echoed, compare it
      // normalized — a casing difference is the same request, a different code
      // is not — and when none is echoed, the status alone stands.
      const confirmedCode =
        typeof data.user_code === 'string' ? normalizeUserCode(data.user_code) : null;
      if (confirmedCode !== null && confirmedCode !== code) {
        throw new Error(
          'The server confirmed a different device code. Re-check the code shown on your device.'
        );
      }

      setSubmitState(null);
      setDone({
        outcome: expectedStatus,
        code,
        clientName: decidedClientName,
        deviceHomeLabel: decidedHomeLabel,
      });
    } catch (err) {
      setSubmitState(null);
      setError(err instanceof Error ? err.message : 'Unexpected error');
    }
  };

  useEffect(() => {
    if (done?.outcome !== 'approved' || !returnTo) return;
    const timeout = window.setTimeout(() => {
      window.location.href = returnTo;
    }, 600);
    return () => window.clearTimeout(timeout);
  }, [done, returnTo]);

  if (sessionLoading || !session) {
    return <FullPageSpinner className="min-h-[calc(100vh-3.5rem)]" />;
  }

  if (done) {
    return (
      <ConsentScreen
        live
        title={
          done.outcome === 'approved'
            ? fromLink
              ? 'Access Authorized'
              : 'Device Authorized'
            : 'Authorization Denied'
        }
        description={
          done.outcome === 'approved'
            ? returnTo
              ? 'You can close this tab and return to the Lobu app.'
              : 'You can close this tab and return to your terminal.'
            : 'The device was not authorized. You can close this tab.'
        }
        review={
          <div className="space-y-4 text-sm">
            {done.outcome === 'approved' && done.deviceHomeLabel ? (
              <p className="text-muted-foreground">
                This device now lives in {done.deviceHomeLabel}. Change its workspace or share its
                connections from the device page under Connectors.
              </p>
            ) : null}
            {/* Correlation evidence: name the exact code and client this
                decision applied to, so the verifier can match it against the
                code their CLI is polling instead of trusting a bare verdict. */}
            <dl className="space-y-1">
              <dt className="text-muted-foreground">
                {done.outcome === 'approved' ? 'Approved code' : 'Denied code'}
              </dt>
              <dd className="font-mono text-base tracking-widest text-foreground">{done.code}</dd>
              {done.clientName ? (
                <>
                  <dt className="text-muted-foreground">Requested by</dt>
                  <dd className="text-foreground">{done.clientName}</dd>
                </>
              ) : null}
            </dl>
          </div>
        }
      />
    );
  }

  const grantSummary = deviceGrantSummary({
    isVerified,
    requiresDeviceHome,
    requiresWorkspaceAccess,
    homeLabel: deviceHomeOrg ? workspaceLabel(deviceHomeOrg) : null,
    workspaceCount: selectedOrgIds.length,
  });

  const codeField = (
    <div className="space-y-2">
      <label htmlFor="user-code" className="text-sm font-medium">
        Device code
      </label>
      <Input
        id="user-code"
        value={userCode}
        disabled={submitState === 'approve' || submitState === 'deny'}
        onChange={(e) => {
          verificationRequest.current += 1;
          setUserCode(e.target.value.toUpperCase());
          setVerification(null);
          resetWorkspaceSelection();
          setChosenDeviceHomeId(null);
          setSubmitState(null);
          setError(null);
        }}
        placeholder="ABCD-1234"
        className="font-mono text-center text-lg tracking-widest"
        maxLength={9}
        autoFocus={!search.user_code}
      />
    </div>
  );

  const deviceWorkspaceSelect = showDeviceHome ? (
    <DeviceWorkspaceSelect
      deviceName={activeVerification?.clientName ?? null}
      orgList={orgList}
      deviceHomeOrgId={deviceHomeOrgId}
      onChange={setChosenDeviceHomeId}
      orgsLoading={orgsLoading}
      orgsError={orgsError}
      onRetry={retryOrgs}
      disabled={submitState !== null}
    />
  ) : null;

  const review = isVerified ? (
    <div className="space-y-5">
      {codeField}
      <AuthorizationReview
        idPrefix="oauth-device"
        clientName={activeVerification?.clientName ?? null}
        clientNote={
          hasDeviceWorkerScope ? 'This device can run commands and control apps.' : undefined
        }
        requestedScopes={verifiedScopes}
        accessLevel={accessLevel}
        onAccessLevelChange={setAccessLevel}
        allowNoAccess={hasDeviceWorkerScope}
        disabled={submitState !== null}
        resource={activeVerification?.resource ?? null}
        logoUri={activeVerification?.logoUri ?? null}
        clientUri={activeVerification?.clientUri ?? null}
      />
      {hasConnectionTokenScope ? (
        <p className="text-sm text-muted-foreground">
          Can fetch tokens for your connected services.
        </p>
      ) : null}
      {isVerified && requiresDeviceHome && !showDeviceHome && deviceHomeOrg ? (
        <p className="text-sm text-muted-foreground">
          This device will live in {workspaceLabel(deviceHomeOrg)}.
          {requiresWorkspaceAccess ? ' MCP access follows the device home.' : ''}
        </p>
      ) : null}
      {deviceWorkspaceSelect && !showWorkspacePicker ? deviceWorkspaceSelect : null}
    </div>
  ) : (
    codeField
  );

  const aside = showWorkspacePicker ? (
    <div className="space-y-4">
      {deviceWorkspaceSelect}
      {requiresDeviceHome && deviceHomeOrg ? (
        <p className="text-xs text-muted-foreground">
          {workspaceLabel(deviceHomeOrg)} is always included as the device home. Additional
          workspaces grant MCP access only.
        </p>
      ) : null}
      <WorkspaceAccessPicker
        idPrefix="oauth-device"
        heading={requiresDeviceHome ? 'Additional workspaces' : undefined}
        allowMultiple={multiWorkspaceGrantsEnabled}
        includedOrgId={requiresDeviceHome ? deviceHomeOrgId : null}
        orgList={orgList}
        selectedOrgIds={selectedOrgIds}
        onWorkspaceAccessChange={setWorkspaceAccess}
        onSelectedOrgChange={toggleSelectedOrgId}
        orgsLoading={orgsLoading}
        orgsError={orgsError}
        onRetry={retryOrgs}
        disabled={submitState !== null}
      />
    </div>
  ) : undefined;

  return (
    <ConsentScreen
      title={isVerified ? 'Authorize application' : 'Connect a device'}
      description={!isVerified ? `Enter the code shown in your ${deviceLabel}.` : undefined}
      busy={submitState !== null}
      error={error}
      review={review}
      aside={aside}
      below={
        isVerified && hasMcpScopes && resourceOrg ? (
          <ResourceScopeNote resourceOrg={resourceOrg} accessLevel={accessLevel} />
        ) : null
      }
      footer={
        isVerified ? (
          <ConsentFooter summary={grantSummary}>
            <ConsentDecisionActions
              busy={submitState === 'approve' || submitState === 'deny' ? submitState : null}
              approveDisabled={submitState !== null || !organizationResolutionReady}
              denyDisabled={submitState !== null}
              onApprove={() => submitConsent(true)}
              onDeny={() => submitConsent(false)}
            />
          </ConsentFooter>
        ) : (
          <ConsentFooter>
            <Button
              type="button"
              onClick={() => verifyDeviceCode(normalizedCode)}
              disabled={submitState !== null || !normalizedCode}
            >
              {submitState === 'verify' ? 'Verifying...' : 'Continue'}
            </Button>
          </ConsentFooter>
        )
      }
    />
  );
}

/**
 * Where a paired device lives. Defaults to the personal workspace; choosing a
 * team workspace homes the device there with its connections private to the
 * user until they share them.
 */
function DeviceWorkspaceSelect({
  deviceName,
  orgList,
  deviceHomeOrgId,
  onChange,
  orgsLoading,
  orgsError,
  onRetry,
  disabled,
}: {
  deviceName: string | null;
  orgList: OrgEntry[];
  deviceHomeOrgId: string | null;
  onChange: (orgId: string) => void;
  orgsLoading: boolean;
  orgsError: string | null;
  onRetry: () => void;
  disabled: boolean;
}) {
  const home = orgList.find((org) => org.id === deviceHomeOrgId) ?? null;
  return (
    <section className="space-y-2 rounded-md border bg-card p-3 text-sm">
      <label htmlFor="oauth-device-home" className="block font-medium text-foreground">
        Device home
      </label>
      {orgsLoading ? (
        <p role="status" aria-live="polite" className="text-muted-foreground">
          Loading your workspaces...
        </p>
      ) : orgsError ? (
        <div role="alert" className="space-y-2 text-destructive">
          <p>Workspaces could not be loaded. Approval is unavailable until they load.</p>
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <select
            id="oauth-device-home"
            value={home?.id ?? ''}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            {home ? null : (
              <option value="" disabled>
                Choose a workspace
              </option>
            )}
            {orgList.map((org) => (
              <option key={org.id} value={org.id}>
                {workspaceLabel(org)}
              </option>
            ))}
          </select>
          {home && !home.isPersonal ? (
            <p role="note" className="rounded-md bg-amber-500/10 px-3 py-2 text-amber-700 dark:text-amber-400">
              {deviceName ?? 'This device'} will be added to {home.name}. Its Shell and Browser
              connections will be private to you — teammates can't use them unless you share
              them from the device page.
            </p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Where this device is registered. Its connectors and Automations run here. A device
            that is already paired keeps its workspace; change it from the Devices page.
          </p>
        </>
      )}
    </section>
  );
}
